import { monitoring } from "@/services/monitoring";

// Global auth context accessor - set by the custom hook
let globalAuthContext: { userId?: string; roles?: string[] } | null = null;

export const setGlobalAuthContext = (context: { userId?: string; roles?: string[] } | null) => {
  globalAuthContext = context;
};

interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  [key: string]: any;
}

/**
 * Centralized error handling utility
 * In development: logs to console
 * In production: sends errors to Sentry and optionally calls setError for UI state management
 */
export const handleError = (
  error: Error | unknown | string,
  context?: ErrorContext,
  setError?: (message: string) => void,
  userMessage?: string
): void => {
  // Convert string errors to Error objects for better stack traces
  const errorObj = typeof error === 'string' ? new Error(error) : error;
  
  // Merge provided context with global auth context
  const enrichedContext = {
    ...context,
    ...(globalAuthContext && {
      userId: globalAuthContext.userId,
      userRoles: globalAuthContext.roles
    })
  };
  
  // In development, log to console instead of sending to Sentry
  if (process.env.NODE_ENV === 'development') {
    console.error('Error:', errorObj);
    if (enrichedContext) {
      console.log('Context:', enrichedContext);
    }
  } else {
    // Send to Sentry in production
    monitoring.captureException(errorObj, enrichedContext);
  }
  
  // Set UI error state if setError function and userMessage are provided
  if (setError && userMessage) {
    setError(userMessage);
  }
};

/**
 * Log a warning message to Sentry
 */
export const logWarning = (
  message: string,
  context?: ErrorContext
): void => {
  monitoring.captureMessage(message, context);
};